/* eslint-disable @typescript-eslint/ban-ts-comment */
import Button from "../../components/Shared/Button";
import Input from "../../components/Shared/Input";
import { useFormik } from "formik";
import { AxiosError } from "axios";
import { ButtonTypes } from "../../utils/consts";
import { useMutation } from "@tanstack/react-query";
import * as Yup from "yup";
import { AddNewChapter } from "../../api/Courses/AddChapter";
import { useState, useRef } from "react";
import { toast } from "react-toastify";
import { queryClient } from "../../main";
import { useParams } from "react-router-dom";
import Upload from "../../components/Shared/Upload";
import { useNavigate } from "react-router-dom";
interface Lecture {
  title: string;
  videos: Videos[];
  resources: Resource[];
}
interface Resource {
  title: string;
  url: string;
}
interface Videos {
  name: string;
  url: string;
}

interface Module {
  id: number;
  name: string;
  description: string;
  lectures: Lecture[];
}

const AddLectures = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [modules, setModules] = useState<Module[]>([
    { id: 1, name: "", description: "", lectures: [] },
  ]);
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  const [isAnyUploading, setIsAnyUploading] = useState(false);
  const uploadingCount = useRef(0);
  const createValidationSchema = () => {
    const moduleSchemas = modules.map((module, index) => {
      const lectureSchemas = module.lectures.map((_, lectureIndex) => ({
        [`title${index}${lectureIndex}`]: Yup.string().required(
          "Lecture title is required"
        ),
        [`videoUrl${index}${lectureIndex}`]: Yup.string().required(
          "Video URL is required"
        ),
      }));

      return Yup.object().shape({
        [`moduleName${index}`]: Yup.string().required(
          "Module name is required"
        ),
        [`moduleDescription${index}`]: Yup.string().required(
          "Module description is required"
        ),
        ...lectureSchemas.reduce((acc, curr) => ({ ...acc, ...curr }), {}),
      });
    });

    return Yup.object().shape({
      modules: Yup.array().of(
        Yup.object().shape(
          moduleSchemas.reduce((acc, curr) => ({ ...acc, ...curr.fields }), {})
        )
      ),
    });
  };

  const addModule = () => {
    setModules([
      ...modules,
      { id: modules.length + 1, name: "", description: "", lectures: [] },
    ]);
  };

  const addLecture = (moduleId: number) => {
    const newModules = modules.map((module) => {
      if (module.id === moduleId) {
        const newLectures = [
          ...module.lectures,
          { title: "", videos: [], resources: [] },
        ];
        return { ...module, lectures: newLectures };
      }
      return module;
    });
    setModules(newModules);
  };
  const addResource = (moduleId: number, lectureIndex: number) => {
    const newModules = modules.map((module) => {
      if (module.id === moduleId) {
        const newResources = [
          ...module.lectures[lectureIndex].resources,
          { title: "", url: "" },
        ];
        const newLectures = module.lectures.map((lecture, idx) => {
          if (idx === lectureIndex) {
            return { ...lecture, resources: newResources };
          }
          return lecture;
        });
        return { ...module, lectures: newLectures };
      }
      return module;
    });
    setModules(newModules);
  };
  const addVideo = (moduleId: number, lectureIndex: number) => {
    const newModules = modules.map((module) => {
      if (module.id === moduleId) {
        const newVideos = [
          ...module.lectures[lectureIndex].videos,
          { name: "", url: "" },
        ];
        const newLectures = module.lectures.map((lecture, idx) => {
          if (idx === lectureIndex) {
            return { ...lecture, videos: newVideos };
          }
          return lecture;
        });
        return { ...module, lectures: newLectures };
      }
      return module;
    });
    setModules(newModules);
  };

  const { mutate, error } = useMutation({
    mutationFn: AddNewChapter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      formik.setSubmitting(false);
      setIsSubmitted(true);
      toast.success("Course Added successfully");
      navigate("/dashboard/courses");
    },
    onError: (error) => {
      toast.error("Error adding course");
      console.error(error);
    },
  });
  const formik = useFormik({
    initialValues: {},
    validationSchema: createValidationSchema(),
    onSubmit: () => {
      const payload = modules.map((module) => ({
        name: module.name,
        description: module.description,
        courseId,
        lectures: module.lectures.map((lecture) => ({
          title: lecture.title,
          videos: lecture.videos,
          resources: lecture.resources,
        })),
      }));
      //@ts-expect-error
      mutate(payload);
    },
  });

  const handleVideosUrlUpload = (
    moduleId: number,
    lectureIndex: number,
    videoIndex: number,
    url: string,
    cipherId?: string,
    duration?: string
  ) => {
    const updatedModules = modules.map((module, modIdx) => {
      if (modIdx === moduleId) {
        const updatedLectures = module.lectures.map((lecture, lecIdx) => {
          if (lecIdx === lectureIndex) {
            const updatedVideos = lecture.videos.map((video, vidIdx) => {
              if (vidIdx === videoIndex) {
                return {
                  ...video,
                  url,
                  duration,
                  ...(cipherId ? { cipherId } : {}),
                };
              }
              return video;
            });
            return { ...lecture, videos: updatedVideos };
          }
          return lecture;
        });
        return { ...module, lectures: updatedLectures };
      }
      return module;
    });
    setModules(updatedModules);
  };

  const handleResourcesUrlUpload = (
    moduleId: number,
    lectureIndex: number,
    resourceIndex: number,
    url: string
  ) => {
    const updatedModules = modules.map((module, modIdx) => {
      if (modIdx === moduleId) {
        const updatedLectures = module.lectures.map((lecture, lecIdx) => {
          if (lecIdx === lectureIndex) {
            const updatedResources = lecture.resources.map(
              (resource, resIdx) => {
                if (resIdx === resourceIndex) {
                  return { ...resource, url: url };
                }
                return resource;
              }
            );
            return { ...lecture, resources: updatedResources };
          }
          return lecture;
        });
        return { ...module, lectures: updatedLectures };
      }
      return module;
    });
    setModules(updatedModules);
  };
  return (
    <form onSubmit={formik.handleSubmit} className="pt-5 pb-20">
      <div className="flex justify-between mb-6">
        <h1 className="text-burntBlack font-bold text-[32px] mt-5">
          Add new lectures
        </h1>
        <Button
          className="!h-11 w-40 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
          variant="primary"
          onClick={() => navigate(-1)}
          type={ButtonTypes.BUTTON}
        >
          Get Back
          {/* {isPending ? (
          <Spinner />) : ('')} */}
        </Button>
      </div>
      <div className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] space-y-5">
        {modules.map((module, moduleIndex) => (
          <div key={moduleIndex} className="flex gap-7">
            <div className="min-w-[671px]">
              <h1 className="font-bold text-2xl">Module {module.id}</h1>
              <div className="space-y-7 pl-4 pr-6 py-5 mt-2 rounded-lg bg-[#F5F6F8]">
                <div className="flex gap-5 w-full">
                  <Input
                    value={module.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setModules(
                        modules.map((m) =>
                          m.id === module.id ? { ...m, name: newName } : m
                        )
                      );
                    }}
                    className="text-lg font-semibold w-full"
                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                    title="Module Name"
                    placeholder=""
                    name="name"
                    type="text"
                  />
                  <Input
                    value={module.description}
                    onChange={(e) => {
                      const newDescription = e.target.value;
                      setModules(
                        modules.map((m) =>
                          m.id === module.id
                            ? { ...m, description: newDescription }
                            : m
                        )
                      );
                    }}
                    className="text-lg font-semibold w-full"
                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                    title="Description"
                    placeholder=""
                    name="description"
                    type="text"
                  />
                </div>
                {module.lectures.map((lecture, lectureIndex) => (
                  <>
                    <div key={lectureIndex} className="flex gap-5 w-full">
                      <Input
                        value={lecture.title}
                        onChange={(e) => {
                          const newTitle = e.target.value;
                          const updatedModules = modules.map((m) => {
                            if (m.id === module.id) {
                              return {
                                ...m,
                                lectures: m.lectures.map((l, i) =>
                                  i === lectureIndex
                                    ? { ...l, title: newTitle }
                                    : l
                                ),
                              };
                            }
                            return m;
                          });
                          setModules(updatedModules);
                        }}
                        className="text-lg font-semibold w-full"
                        inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                        title={`Lecture ${lectureIndex + 1} Name`}
                        placeholder=""
                        name={`title${lectureIndex}`}
                        type="text"
                      />
                    </div>
                    {lecture.videos.map((video, videoIndex) => (
                      <div key={videoIndex} className="flex gap-5 w-full">
                        <Input
                          value={video.name}
                          onChange={(e) => {
                            const newName = e.target.value;
                            const updatedModules = modules.map((m, idx) => {
                              if (idx === moduleIndex) {
                                return {
                                  ...m,
                                  lectures: m.lectures.map((l, li) => {
                                    if (li === lectureIndex) {
                                      return {
                                        ...l,
                                        videos: l.videos.map((r, ri) => {
                                          if (ri === videoIndex) {
                                            return { ...r, name: newName };
                                          }
                                          return r;
                                        }),
                                      };
                                    }
                                    return l;
                                  }),
                                };
                              }
                              return m;
                            });
                            setModules(updatedModules);
                          }}
                          className="text-lg font-semibold w-full"
                          inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                          title={`Video ${videoIndex + 1} Title`}
                          placeholder=""
                          name={`videoTitle${moduleIndex}${lectureIndex}${videoIndex}`}
                          type="text"
                        />

                        <Upload
                          acceptedFiles={{
                            "video/mp4": [],
                            "video/webm": [],
                            "video/ogg": [],
                            "video/x-matroska": [],
                          }}
                          isVideo
                          folder="courses_videos"
                          onUploadingChange={(isUploading) => {
                            if (isUploading) {
                              uploadingCount.current += 1;
                            } else {
                              uploadingCount.current = Math.max(
                                0,
                                uploadingCount.current - 1
                              );
                            }
                            setIsAnyUploading(uploadingCount.current > 0);
                          }}
                          onFileUploaded={(url, key, duration) => {
                            handleVideosUrlUpload(
                              moduleIndex,
                              lectureIndex,
                              videoIndex,
                              url,
                              key,
                              duration
                            );
                          }}
                        >
                          <Input
                            upload={true}
                            value={video.url}
                            onChange={(e) => {
                              const newUrl = e.target.value;
                              const updatedModules = modules.map((m, idx) => {
                                if (idx === moduleIndex) {
                                  return {
                                    ...m,
                                    lectures: m.lectures.map((l, li) => {
                                      if (li === lectureIndex) {
                                        return {
                                          ...l,
                                          videos: l.videos.map((r, ri) => {
                                            if (ri === videoIndex) {
                                              return { ...r, url: newUrl };
                                            }
                                            return r;
                                          }),
                                        };
                                      }
                                      return l;
                                    }),
                                  };
                                }
                                return m;
                              });
                              setModules(updatedModules);
                            }}
                            className="text-lg font-semibold w-full"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            title={`Lecture ${lectureIndex + 1} Video`}
                            placeholder=""
                            name={`video${moduleIndex}${lectureIndex}${videoIndex}`}
                            type="text"
                          />
                        </Upload>
                      </div>
                    ))}
                    <Button
                      type={ButtonTypes.BUTTON}
                      disabled={formik.isSubmitting}
                      variant="primary"
                      className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-10 w-40 px-1 text-lg font-bold"
                      onClick={() => addVideo(module.id, lectureIndex)}
                    >
                      Add Video
                    </Button>

                    {lecture.resources.map((resource, resourceIndex) => (
                      <div key={resourceIndex} className="flex gap-5">
                        <Input
                          value={resource.title}
                          onChange={(e) => {
                            const newTitle = e.target.value;
                            const updatedModules = modules.map((m, idx) => {
                              if (idx === moduleIndex) {
                                return {
                                  ...m,
                                  lectures: m.lectures.map((l, li) => {
                                    if (li === lectureIndex) {
                                      return {
                                        ...l,
                                        resources: l.resources.map((r, ri) => {
                                          if (ri === resourceIndex) {
                                            return { ...r, title: newTitle };
                                          }
                                          return r;
                                        }),
                                      };
                                    }
                                    return l;
                                  }),
                                };
                              }
                              return m;
                            });
                            setModules(updatedModules);
                          }}
                          className="text-lg font-semibold w-full"
                          inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                          title={`Resource ${resourceIndex + 1} Title`}
                          placeholder=""
                          name={`resourceTitle${moduleIndex}${lectureIndex}${resourceIndex}`}
                          type="text"
                        />
                        <Upload
                          acceptedFiles={{ pdf: [] }}
                          folder="courses_resources"
                          onUploadingChange={(isUploading) => {
                            if (isUploading) {
                              uploadingCount.current += 1;
                            } else {
                              uploadingCount.current = Math.max(
                                0,
                                uploadingCount.current - 1
                              );
                            }
                            setIsAnyUploading(uploadingCount.current > 0);
                          }}
                          onFileUploaded={(url) =>
                            handleResourcesUrlUpload(
                              moduleIndex,
                              lectureIndex,
                              resourceIndex,
                              url
                            )
                          }
                        >
                          <Input
                            upload={true}
                            value={resource.url}
                            onChange={(e) => {
                              const newUrl = e.target.value;
                              const updatedModules = modules.map((m, idx) => {
                                if (idx === moduleIndex) {
                                  return {
                                    ...m,
                                    lectures: m.lectures.map((l, li) => {
                                      if (li === lectureIndex) {
                                        return {
                                          ...l,
                                          resources: l.resources.map(
                                            (r, ri) => {
                                              if (ri === resourceIndex) {
                                                return { ...r, url: newUrl };
                                              }
                                              return r;
                                            }
                                          ),
                                        };
                                      }
                                      return l;
                                    }),
                                  };
                                }
                                return m;
                              });
                              setModules(updatedModules);
                            }}
                            className="text-lg font-semibold w-full"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            title={`Resource ${resourceIndex + 1} URL`}
                            placeholder=""
                            name={`resourceUrl${moduleIndex}${lectureIndex}${resourceIndex}`}
                            type="text"
                          />
                        </Upload>
                      </div>
                    ))}
                    <Button
                      type={ButtonTypes.BUTTON}
                      disabled={formik.isSubmitting}
                      variant="primary"
                      className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-10 w-40 px-1 text-lg font-bold"
                      onClick={() => addResource(module.id, lectureIndex)}
                    >
                      Add Resource
                    </Button>
                  </>
                ))}
                <Button
                  type={ButtonTypes.BUTTON}
                  onClick={() => addLecture(module.id)} // Ensure this function is defined and correctly adds a resource
                  disabled={formik.isSubmitting}
                  variant="primary"
                  className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-10 w-40 px-1 text-lg font-bold"
                >
                  Add Lecture
                </Button>
                {error instanceof AxiosError && (
                  <span className="text-red-500 font-bold text-lg">
                    {typeof error?.response?.data?.message === "string"
                      ? error?.response?.data?.message
                      : "An error occurred"}
                  </span>
                )}
              </div>
            </div>
            <div className="max-w-[364px]">
              <h1 className="mb-2.5 font-bold text-2xl text-primary">
                Course Contnet
              </h1>
              <p className="text-lightGray font-medium">
                Add your modules here. Every modules overage 30-60 minutes,
                divided into short video lessons of 2-15 minutes(almost)
              </p>
            </div>
          </div>
        ))}

        <div className="pt-6 flex gap-9">
          <Button
            type={ButtonTypes.BUTTON}
            onClick={addModule}
            variant="primary"
            className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-10 w-40 px-1 text-lg font-bold"
          >
            Add Module
          </Button>
          <Button
            type={ButtonTypes.SUBMIT}
            disabled={formik.isSubmitting || isSubmitted || isAnyUploading}
            variant="primary"
            className={
              `!h-10 w-40 px-1 text-lg font-bold ` +
              (formik.isSubmitting || isSubmitted || isAnyUploading
                ? "!bg-gray-300 !text-gray-500 cursor-not-allowed border border-gray-300"
                : "!bg-[#4379EE] !text-white hover:!bg-primary")
            }
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AddLectures;
